import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type { BillGetOneResponse } from "@/services/core-api/types"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface DeleteBillInput {
  billId: number
  organizationId: Organization["id"]
}

export const useDeleteBill = (
  options?: UseMutationOptions<BillGetOneResponse, HttpError, DeleteBillInput>
) => {
  const { invalidateQueries } = useInvalidateQueries()

  const mutation = useMutation<BillGetOneResponse, HttpError, DeleteBillInput>({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({ billId, organizationId }: DeleteBillInput) => {
      try {
        const response = await httpClient.delete<BillGetOneResponse>(
          `/api/v1/organizations/${organizationId}/bills/${billId}`
        )
        return response.data
      } catch (error: unknown) {
        throw toHttpError(error)
      }
    },
    onError: (...args) => {
      options?.onError?.(...args)
    },
    onSuccess: (data, ...rest) => {
      invalidateQueries([
        createQueryKey(ApiQueryName.Bill, "getOne"),
        createQueryKey(ApiQueryName.Bills, "getMany"),
        createQueryKey(ApiQueryName.BillPeriods, "getMany"),
        createQueryKey(ApiQueryName.BillPeriod, "getOne"),
      ])

      options?.onSuccess?.(data, ...rest)
    },
  })

  return useRenameQueryResult(mutation, "deleteBill")
}
